'use client';
import classNames from 'classnames';
import get from 'lodash/get.js';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import visuallyHiddenStyles from '../../style/display/visually-hidden.module.scss';
import staticContentPropCheck from '../../utils/content/staticContentPropCheck.js';
import env from '../../utils/env/env.js';
import detectTouchPoint from '../../utils/touch/detectTouchPoint.js';
import { HelpOutlined } from '../icons/HelpOutlined.js';

import styles from './tooltip.module.scss';

export const TOOLTIP_OPENED_EVENT_NAME = 'jl-ui-kit.tooltip.opened';

const Tooltip = ({
  showContent = false,
  popoverClassname = undefined,
  id = 'tooltipID',
  buttonLabel = '',
  anchor = 'center',
  verticalAnchor = '',
  'data-testid': dataTestId = '',
  icon = false,
  modalHeaderClassName = '',
  onOpen = () => {},
  onClose = () => {},
  ariaLabel,
  ariaDescribedBy,
  children,
}) => {
  // State for managing tooltip content visibility, vertical alignment, and scroll position
  const [contentOpen, setContentOpen] = useState(showContent);
  const [currentVerticalAnchor, setCurrentVerticalAnchor] = useState(
    verticalAnchor || 'top'
  );
  const [scrollStartPosition, setScrollStartPosition] = useState(0);

  // Refs for the tooltip button and popover
  const tooltipRef = useRef(null);
  const tooltipPopoverRef = useRef(null);

  // Handler to open the tooltip
  const openTooltip = useCallback(() => {
    setContentOpen(true);

    if (env.isClient) {
      window.addEventListener('click', windowClickListener);
      window.addEventListener('keydown', windowEscapeListener);
      window.dispatchEvent(
        new CustomEvent(TOOLTIP_OPENED_EVENT_NAME, {
          detail: { id },
        })
      );
    }
  }, [id]);

  // Handler to close the tooltip
  const closeTooltip = useCallback(
    (tooltipClosedManually = false) => {
      if (!contentOpen) {
        return;
      }

      setContentOpen(false);
      onClose(tooltipClosedManually);

      if (env.isClient) {
        window.removeEventListener('click', windowClickListener);
        window.removeEventListener('keydown', windowEscapeListener);
        if (get(tooltipRef, 'current.querySelector')) {
          const launchButton = tooltipRef.current.querySelector('button');
          if (launchButton && launchButton.focus) {
            launchButton.focus();
          }
        }
      }
    },
    [contentOpen, onClose]
  );

  // Handler to manage the tooltip popover close
  const onPopOverClose = (e) => {
    const tooltipClosedManually =
      e.currentTarget.dataset?.test === 'tooltip-popover-close-button';
    closeTooltip(tooltipClosedManually);
  };

  // Handler to manage click events
  const onClick = () => {
    if (contentOpen) {
      closeTooltip(true);
    } else {
      openTooltip();
    }
  };

  // Handler to manage button touch start
  const onButtonTouchStart = () => {
    setScrollStartPosition(window.scrollY);
  };

  // Handler to manage button touch end
  const onButtonTouchEnd = (evt) => {
    evt.preventDefault();

    const scrollEndPosition = window.scrollY;

    if (!(scrollEndPosition > scrollStartPosition + 5)) {
      if (contentOpen) {
        closeTooltip(true);
        return;
      }

      openTooltip();
    }
  };

  // Handler for click events outside the tooltip
  const windowClickListener = (event) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
      closeTooltip();
    }
  };

  // Handler for escape key press
  const windowEscapeListener = (event) => {
    if (tooltipRef.current && event.key === 'Escape') {
      closeTooltip(true);
    }
  };

  // Handler to check if the tooltip should close on page area touch
  const pageAreaCheck = (e) => {
    if (tooltipPopoverRef.current) {
      if (!detectTouchPoint(tooltipPopoverRef.current, e)) {
        onPopOverClose(e);
      }
    }
  };

  // Callback for intersection observer
  const intersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio !== 1 && contentOpen) {
        setContentOpen(false);
      }
    });
  };

  // Initial setup of intersection observer
  const initIntersectionObserver = useCallback(() => {
    if (tooltipRef.current) {
      const observer = new IntersectionObserver(intersectionObserverCallback, {
        threshold: 1,
      });
      observer.observe(tooltipRef.current);
      return observer;
    }
  }, [contentOpen]);

  // Cleanup of intersection observer
  const removeIntersectionObserver = useCallback((observer) => {
    if (observer && tooltipRef.current) {
      observer.unobserve(tooltipRef.current);
    }
  }, []);

  // Position the tooltip popover based on space availability
  const positionTooltipPopover = useCallback(() => {
    const defaultMargin = 16;

    if (tooltipRef.current && tooltipPopoverRef.current) {
      const tooltipPosition = tooltipRef.current.getBoundingClientRect();
      const availableSpaceAbove = tooltipPosition.top;
      const tooltipPopoverHeight =
        tooltipPopoverRef.current.clientHeight + defaultMargin;

      let modalSpaceAbove = 0;
      if (modalHeaderClassName) {
        const modalHeader = document.querySelector(`.${modalHeaderClassName}`);
        if (modalHeader) {
          const modalHeaderPosition = modalHeader.getBoundingClientRect();
          modalSpaceAbove = modalHeaderPosition.bottom;
        }
      }

      const verticalAnchor =
        tooltipPopoverHeight < availableSpaceAbove - modalSpaceAbove
          ? 'top'
          : 'bottom';

      setCurrentVerticalAnchor(verticalAnchor);
    }
  }, [modalHeaderClassName]);

  useEffect(() => {
    if (contentOpen) {
      positionTooltipPopover();
      onOpen();
      if (env.isClient) {
        window.addEventListener('click', windowClickListener);
        window.addEventListener('keydown', windowEscapeListener);
      }
    }

    if (env.isClient) {
      window.addEventListener('resize', positionTooltipPopover);
      window.addEventListener('touchstart', pageAreaCheck);
      window.addEventListener(TOOLTIP_OPENED_EVENT_NAME, onOpen);
      const observer = initIntersectionObserver();
      return () => {
        window.removeEventListener('click', windowClickListener);
        window.removeEventListener('keydown', windowEscapeListener);
        window.removeEventListener('resize', positionTooltipPopover);
        window.removeEventListener('touchstart', pageAreaCheck);
        window.removeEventListener(TOOLTIP_OPENED_EVENT_NAME, onOpen);
        removeIntersectionObserver(observer);
      };
    }
  }, [
    contentOpen,
    onOpen,
    pageAreaCheck,
    positionTooltipPopover,
    removeIntersectionObserver,
    initIntersectionObserver,
    windowClickListener,
    windowEscapeListener,
  ]);

  return (
    <div
      ref={tooltipRef}
      aria-label={ariaLabel}
      className={classNames(styles['c-tooltip'], {
        [styles['c-tooltip--under']]: currentVerticalAnchor === 'bottom',
        [styles['c-tooltip--open']]: contentOpen,
      })}
      data-jlds={true}
      data-testid={dataTestId}
      role="tooltip"
    >
      <button
        aria-describedby={ariaDescribedBy}
        aria-label="tooltip"
        className={classNames(styles['c-tooltip__button'], {
          [styles['c-tooltip__button--with-icon']]: icon || buttonLabel === '',
        })}
        data-testid="tooltip-text-button"
        onClick={onClick}
        onTouchEnd={onButtonTouchEnd}
        onTouchStart={onButtonTouchStart}
        type="button"
      >
        {buttonLabel && <span>{buttonLabel}</span>}
        {(icon || buttonLabel === '') && <HelpOutlined size="large" />}
      </button>
      {contentOpen && (
        <div
          className={classNames(styles['c-tooltip-popover'], {
            [styles['c-tooltip-popover--top']]: currentVerticalAnchor === 'top',
            [styles['c-tooltip-popover--bottom']]:
              currentVerticalAnchor === 'bottom',
            [styles['c-tooltip-popover--left']]: anchor === 'left',
            [styles['c-tooltip-popover--right']]: anchor === 'right',
            [styles['c-tooltip-popover--center']]: anchor === 'center',
            [styles['c-tooltip-popover--with-icon']]:
              icon || buttonLabel === '',
            [styles['c-tooltip-popover--with-label']]: buttonLabel,
          })}
        >
          <div
            ref={tooltipPopoverRef}
            className={classNames(
              popoverClassname,
              styles['c-tooltip-popover__inner']
            )}
            data-testid="tooltip-popover-inner"
          >
            <button
              className={styles['c-tooltip-popover__closeButton']}
              data-testid="tooltip-popover-close-button"
              onClick={onPopOverClose}
            >
              <span className={visuallyHiddenStyles.visuallyHidden}>
                Close information
              </span>
              <span
                className={styles['c-tooltip-popover__closeButtonIcon']}
                data-testid="tooltip-icon"
              />
            </button>

            <div
              className={styles['c-tooltip-popover__content']}
              data-testid="tooltip-popover-content"
              role="alert"
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  showContent: PropTypes.bool,
  popoverClassname: PropTypes.string,
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  buttonLabel: staticContentPropCheck,
  anchor: PropTypes.oneOf(['left', 'center', 'right']),
  verticalAnchor: PropTypes.oneOf(['top', 'bottom', '']),
  'data-testid': PropTypes.string,
  icon: PropTypes.bool,
  modalHeaderClassName: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
};

export default Tooltip;
